/**
 * Unified message bubble component for timeline threads.
 * Handles both colored lane threads and gray "other threads" lane.
 */

import React, { useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmailIcon from "@mui/icons-material/Email";
import { format } from "date-fns";
import type { TimelineEvent } from "../types";
import { decodeHtmlEntities, extractEmail } from "../utils";

interface TimelineMessageProps {
  event: TimelineEvent;
  lane: {
    color: string;
    xPosition: number; // Pre-computed X position for this lane
  };
  showSubjectInline: boolean;
  isSubjectHidden: boolean;
  showSender: boolean;
  showDateBullet: boolean;
  isSameSenderAsPrevious: boolean;
  isFirstOfThreadChunk: boolean;
  onOpenEmail?: (emailId: string) => void;
  onDownloadAttachment?: (
    emailId: string,
    attachmentId: string,
    filename?: string
  ) => void;
  laneThickness: number;
  bubbleWidth: number;
  bubbleBaseOffset: number;
  bubbleReplyOffsetPercent: number;
  dateColumnWidth: number;
  datePosition?: 'left' | 'center';
  refCallback: (el: HTMLDivElement | null) => void;
  // Optional: highlight this message with a fading blue border
  isHighlighted?: boolean;
}

/**
 * Renders a message bubble with sender tab, content, and attachments.
 * Used for both colored lane threads and gray "other threads" lane.
 */
export function TimelineMessage({
  event,
  lane,
  showSubjectInline,
  isSubjectHidden,
  showSender,
  showDateBullet,
  isSameSenderAsPrevious,
  isFirstOfThreadChunk,
  onOpenEmail,
  onDownloadAttachment,
  laneThickness,
  bubbleWidth,
  bubbleBaseOffset,
  bubbleReplyOffsetPercent,
  dateColumnWidth,
  datePosition = 'left',
  refCallback,
  isHighlighted = false,
}: TimelineMessageProps) {
  const isOwnDomain = event.fromOwnDomain ?? false;
  const eventDate = event.date ? new Date(event.date) : null;
  const senderEmail = event.emailFrom?.[0]
    ? extractEmail(event.emailFrom[0])
    : "Unknown sender";
  const displayTime = eventDate ? format(eventDate, "h:mma, MMM d") : "";
  const rawSnippet = event.preview
    ? decodeHtmlEntities(event.preview)
    : event.snippet
      ? decodeHtmlEntities(event.snippet)
      : event.name || "No content";
  // Remove newlines for single-line display and limit to 120 chars
  const cleanedSnippet = rawSnippet
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const snippetText =
    cleanedSnippet.length > 120
      ? `${cleanedSnippet.slice(0, 120)}...`
      : cleanedSnippet;

  const marginBottom = isSameSenderAsPrevious ? 0.5 : 2;
  const baseMarginTop = isFirstOfThreadChunk ? 3.75 : 0; // 30px when thread changes
  const marginTop = baseMarginTop + (showDateBullet ? 1.25 : 0); // +10px for date separator

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      key={event.emailId}
      ref={refCallback}
      sx={{
        position: "relative",
        mb: marginBottom,
        mt: marginTop,
        display: "flex",
        alignItems: "flex-start",
        pl: `${dateColumnWidth}px`,
      }}
    >
      {/* Date separator on the left with horizontal line between messages */}
      {showDateBullet && eventDate && (
        <>
          {/* Horizontal line across the timeline - positioned at top margin between messages */}
          <Box
            sx={{
              position: "absolute",
              left: datePosition === 'center' ? 0 : dateColumnWidth,
              right: 0,
              top: marginTop > 0 ? -marginTop * 4 : -8, // Position in the gap between messages
              height: "1px",
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              zIndex: -1, // Below lane lines
            }}
          />
          {/* Date text */}
          <Box
            sx={{
              position: "absolute",
              ...(datePosition === 'center'
                ? {
                    left: 0,
                    right: 0,
                    top: marginTop > 0 ? -marginTop * 4 - 8 : -16,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 1,
                  }
                : {
                    left: 0,
                    top: marginTop > 0 ? -marginTop * 4 - 8 : -16,
                    width: dateColumnWidth,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 1,
                  }),
            }}
          >
            <Typography
              sx={{
                color: "#616161",
                fontSize: "11px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...(datePosition === 'center' && {
                  backgroundColor: "#ffffff",
                  px: 1,
                }),
              }}
            >
              {format(eventDate, "MMM d")}
            </Typography>
          </Box>
        </>
      )}
      {/* Main message content - z-index 3 to appear above lane lines */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          zIndex: 3,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Inline subject - positioned at lane line, left-aligned */}
        {showSubjectInline && event.emailSubject && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              ml: `${lane.xPosition}px`,
              mr: 1,
              visibility: isSubjectHidden ? "hidden" : "visible",
              overflow: "hidden",
              width: `calc(100% - ${lane.xPosition}px - 8px)`,
            }}
          >
            <Box
              sx={{
                width: laneThickness,
                height: laneThickness,
                borderRadius: "50%",
                backgroundColor: lane.color,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: lane.color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {decodeHtmlEntities(event.emailSubject)}
            </Typography>
          </Box>
        )}

        {/* Content area */}
        <Box
          sx={{
            ml: `calc(${bubbleBaseOffset + lane.xPosition}px + ${isOwnDomain ? 0 : bubbleReplyOffsetPercent}%)`,
            width: `${bubbleWidth}%`,
            display: "flex",
            flexDirection: "column",
            alignItems: isOwnDomain ? "flex-start" : "flex-end",
          }}
        >
          {/* Sender email - colored label tab */}
          {showSender && (
            <Box
              sx={{
                backgroundColor: lane.color,
                color: "white",
                fontSize: "11px",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: "8px 8px 0 0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "60%",
              }}
            >
              {senderEmail}
            </Box>
          )}

          {/* Message bubble with colored border */}
          <Box
            onClick={() => event.emailId && onOpenEmail?.(event.emailId)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              alignSelf: "stretch",
              position: "relative",
              backgroundColor:
                isHovered && event.emailId ? "#EEEEEE" : "#FAFAFA",
              border: `3px solid ${lane.color}`,
              borderRadius: showSender
                ? isOwnDomain
                  ? "0 12px 12px 12px"
                  : "12px 0 12px 12px"
                : "12px",
              padding: 1.5,
              cursor: event.emailId && onOpenEmail ? "pointer" : "default",
              transition: "background-color 0.15s ease",
              // Highlight animation when scrolling to this message from chat
              ...(isHighlighted && {
                animation: "highlightFade 4s ease-out forwards",
                "@keyframes highlightFade": {
                  "0%": {
                    backgroundColor: "rgba(255, 235, 59, 0.6)",
                    boxShadow: "0 0 20px 8px rgba(255, 235, 59, 0.5)",
                  },
                  "70%": {
                    backgroundColor: "rgba(255, 235, 59, 0.4)",
                    boxShadow: "0 0 15px 5px rgba(255, 235, 59, 0.3)",
                  },
                  "100%": {
                    backgroundColor: "#FAFAFA",
                    boxShadow: "none",
                  },
                },
              }),
            }}
          >
            {/* "View email" overlay on hover */}
            {isHovered && event.emailId && onOpenEmail && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: "11px",
                  fontWeight: 500,
                  pointerEvents: "none",
                }}
              >
                View email
                <EmailIcon sx={{ fontSize: 14 }} />
              </Box>
            )}
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "text.primary",
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {snippetText}
            </Typography>

            {/* Attachment chips */}
            {event.attachments && event.attachments.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {event.attachments.map(
                  (att: {
                    filename?: string;
                    description?: string;
                    emailAttachmentId: string;
                    contentType?: string;
                    preview?: string;
                  }) => {
                    const isEmailAttachment =
                      att.contentType === "message/rfc822";

                    // Format email attachment preview
                    let emailPreviewContent: React.ReactNode = null;
                    if (isEmailAttachment) {
                      if (att.preview) {
                        // Filter out "From: " line and empty lines
                        const lines = att.preview
                          .split("\n")
                          .filter((line) => line.trim())
                          .filter(
                            (line) => !line.toLowerCase().startsWith("from:")
                          );
                        const firstLine = lines[0] || "";
                        const isSubjectLine = firstLine
                          .toLowerCase()
                          .startsWith("subject:");

                        // Extract subject line (remove "Subject: " prefix if present)
                        const subjectText = isSubjectLine
                          ? firstLine.replace(/^subject:\s*/i, "").trim()
                          : firstLine;

                        // Rest of the content: everything after first line, joined without newlines
                        const restOfContent = lines.slice(1).join(" ").trim();

                        emailPreviewContent = (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              maxWidth: "200px",
                            }}
                          >
                            {/* Subject line - bold, single line with ellipsis */}
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "10px",
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {subjectText || "No subject"}
                            </Typography>
                            {/* Rest of content - normal weight, single line with ellipsis */}
                            {restOfContent && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "10px",
                                  fontWeight: 400,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: "text.secondary",
                                }}
                              >
                                {restOfContent}
                              </Typography>
                            )}
                          </Box>
                        );
                      } else {
                        emailPreviewContent = "Email attachment";
                      }
                    }

                    return isEmailAttachment ? (
                      <Box
                        key={att.emailAttachmentId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (event.emailId && onOpenEmail) {
                            onOpenEmail(event.emailId);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.5,
                          p: 0.75,
                          border: "1px solid #BDBDBD",
                          borderRadius: 1,
                          cursor: onOpenEmail ? "pointer" : "default",
                          "&:hover": {
                            border: "1px solid #000000",
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      >
                        <EmailIcon
                          sx={{ fontSize: 14, color: "#666", mt: 0.25 }}
                        />
                        {emailPreviewContent}
                      </Box>
                    ) : (
                      <Chip
                        key={att.emailAttachmentId}
                        label={att.filename || "Attachment"}
                        size="small"
                        variant="outlined"
                        icon={
                          <AttachFileIcon
                            sx={{ fontSize: "14px !important" }}
                          />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (event.emailId && onDownloadAttachment) {
                            onDownloadAttachment(
                              event.emailId,
                              att.emailAttachmentId,
                              att.filename
                            );
                          }
                        }}
                        sx={{
                          height: "22px",
                          fontSize: "10px",
                          cursor: onDownloadAttachment ? "pointer" : "default",
                          border: "1px solid #BDBDBD",
                          "&:hover": {
                            border: "1px solid #000000",
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    );
                  }
                )}
              </Box>
            )}

            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color: "text.disabled",
                display: "block",
                mt: 1,
                textAlign: isOwnDomain ? "left" : "right",
              }}
            >
              {displayTime}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
